import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import { Button } from "../../../Ui_Components/Button";
import { UploadIngredientBulkResponse } from "../../../../services/ingredients";
import BulkUploadResultView from "./BulkUploadResultView";

interface BulkUploadPreviewModalProps {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onImport: (file: File) => void;
  /* When set, the sheet preview is replaced with a formatted result view
     so the user reads added/updated counts, error rows or header
     mismatches in the same popup they uploaded from. */
  uploadResult?: UploadIngredientBulkResponse | null;
  /* True while the upload API is in flight — disables Import and shows a
     loading state on the button. */
  uploadInFlight?: boolean;
  /* Controls the primary button copy so this modal can back both flows:
     "import" → Import, "modify" → Validate. */
  mode?: "import" | "modify";
  /* Wired by the parent to re-post the picked sheet with
     ?download_errors=true and stream the annotated Excel back to the
     browser. Passed through to BulkUploadResultView which shows an
     inline "Download error file" button on any failure state. */
  onDownloadErrors?: () => Promise<void>;
}

// Parsed representation of one worksheet. `rows` includes every filled cell
// row from the sheet; each row is a string array padded to `columnCount`.
interface ParsedSheet {
  name: string;
  rows: string[][];
  columnCount: number;
}

const ZOOM_OPTIONS = [50, 60, 70, 80, 90, 100, 125];

// Convert a 0-based column index into Excel-style A/B/.../Z/AA notation for
// fallback headers when the workbook has no first-row labels.
const toColumnLabel = (n: number): string => {
  let result = "";
  let value = n;
  while (value >= 0) {
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26) - 1;
  }
  return result;
};

const parseWorkbook = (workbook: XLSX.WorkBook): ParsedSheet[] => {
  // SheetJS exposes per-sheet visibility on `workbook.Workbook.Sheets[i].Hidden`:
  //   0 = visible, 1 = hidden (Excel: right-click → Unhide), 2 = very hidden
  //   (only accessible via VBA). The Bulk upload template ships with helper
  //   sheets marked hidden — surfacing them in the preview confused users
  //   who only expected to see the sheets they had filled in.
  const visibility = workbook.Workbook?.Sheets ?? [];
  return workbook.SheetNames.reduce<ParsedSheet[]>((acc, name, index) => {
    if ((visibility[index]?.Hidden ?? 0) !== 0) return acc;
    const sheet = workbook.Sheets[name];
    // sheet_to_json with `header: 1` returns each row as a raw array,
    // preserving positional columns even when cells are empty.
    const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      defval: "",
      blankrows: true,
    });
    const rows = raw.map((r) =>
      Array.isArray(r) ? r.map((cell) => (cell == null ? "" : String(cell))) : [],
    );
    const columnCount = rows.reduce((max, r) => Math.max(max, r.length), 0);
    // Pad every row out to columnCount so the table renders as a uniform grid.
    const padded = rows.map((r) => {
      const out = [...r];
      while (out.length < columnCount) out.push("");
      return out;
    });
    acc.push({ name, rows: padded, columnCount });
    return acc;
  }, []);
};

const BulkUploadPreviewModal: React.FC<BulkUploadPreviewModalProps> = ({
  open,
  file,
  onClose,
  onImport,
  uploadResult = null,
  uploadInFlight = false,
  mode = "import",
  onDownloadErrors,
}) => {
  const showingResult = uploadResult !== null;
  const primaryLabel = mode === "modify" ? "Validate" : "Import";
  const inFlightLabel = mode === "modify" ? "Validating..." : "Uploading...";
  const [sheets, setSheets] = useState<ParsedSheet[]>([]);
  const [activeSheetName, setActiveSheetName] = useState<string>("");
  const [zoom, setZoom] = useState<number>(100);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Parse the workbook whenever a new file is opened. Runs off the main
  // thread via ArrayBuffer; the SheetJS `read` call itself is synchronous.
  useEffect(() => {
    if (!open || !file) {
      setSheets([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    file
      .arrayBuffer()
      .then((buffer) => {
        if (cancelled) return;
        const workbook = XLSX.read(buffer, { type: "array" });
        const parsed = parseWorkbook(workbook);
        setSheets(parsed);
        setActiveSheetName(parsed[0]?.name ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[BulkUploadPreview] parse failed", err);
        setError("Could not read this file. Please make sure it's a valid Excel workbook.");
        setSheets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, file]);

  const activeSheet = useMemo<ParsedSheet | undefined>(
    () => sheets.find((s) => s.name === activeSheetName),
    [sheets, activeSheetName],
  );

  if (!open || !file) return null;

  // Build the grid to render. Uses the sheet's first row as column headers
  // when present; falls back to A/B/C… labels. Data rows start at index 1.
  // Only render what's actually in the sheet — the container scrolls as
  // needed for larger workbooks.
  const visibleColumnCount = activeSheet?.columnCount ?? 0;
  const headerRow: string[] = (() => {
    const first = activeSheet?.rows[0] ?? [];
    const headers: string[] = [];
    for (let i = 0; i < visibleColumnCount; i++) {
      const cell = first[i]?.trim();
      headers.push(cell || toColumnLabel(i));
    }
    return headers;
  })();
  const dataRows = activeSheet ? activeSheet.rows.slice(1) : [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Fixed popup size — stays constant across sheet switches so the
          dialog never resizes when the active sheet has fewer rows/columns. */}
      <div className="bg-white rounded-md shadow-xl w-[90vw] h-[92vh] flex flex-col">
        {/* Header — file name */}
        <div className="px-6 pt-5 pb-4 flex-shrink-0">
          <h2 className="text-lg sm:text-xl 3xl:text-[22px] font-semibold font-inter text-[#1F1F1F]">
            {file.name}
          </h2>
        </div>

        {/* Body — swaps to the API result view once the backend responds
            so the outcome renders in the same popup the user uploaded from. */}
        {showingResult ? (
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            <BulkUploadResultView
              result={uploadResult!}
              onDownloadErrors={onDownloadErrors}
            />
          </div>
        ) : (
        <div className="flex-1 overflow-hidden px-6 flex flex-col min-h-0">
          <div className="flex-1 overflow-auto border border-[#E5E7EB] rounded-sm custom-scrollbar-overlay">
            {loading ? (
              <div className="h-full flex items-center justify-center py-16">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500 font-inter">
                    Reading workbook…
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="h-full flex items-center justify-center py-16">
                <p className="text-red-500 text-sm sm:text-base font-inter">
                  {error}
                </p>
              </div>
            ) : (
              <table
                className="border-collapse text-xs sm:text-sm font-inter text-[#1F1F1F]"
                style={{ fontSize: `${(zoom / 100) * 13}px` }}
              >
                <thead>
                  <tr>
                    <th className="sticky top-0 left-0 z-20 bg-[#F3F4F6] border border-[#E5E7EB] w-10 h-9" />
                    {headerRow.map((label, i) => (
                      <th
                        key={i}
                        className="sticky top-0 z-10 bg-[#F3F4F6] border border-[#E5E7EB] font-medium text-left px-3 py-2 min-w-[110px]"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, r) => (
                    <tr key={r}>
                      <td className="sticky left-0 z-10 bg-[#F9FAFB] border border-[#E5E7EB] text-center text-[#6B7280] w-10 h-8">
                        {r + 1}
                      </td>
                      {Array.from({ length: visibleColumnCount }, (_, c) => (
                        <td
                          key={c}
                          className="border border-[#E5E7EB] h-8 min-w-[110px] px-2 whitespace-nowrap overflow-hidden text-ellipsis"
                        >
                          {row[c] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Zoom + sheet tabs strip */}
          <div className="flex items-center gap-3 py-2 border-b border-[#E5E7EB] mt-0 flex-shrink-0">
            {/* Zoom picker */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setZoomOpen((v) => !v)}
                className="flex items-center gap-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs sm:text-sm font-inter text-[#1F1F1F] hover:bg-gray-50 transition-colors"
              >
                {zoom}%
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  className={`transition-transform ${zoomOpen ? "rotate-180" : ""}`}
                >
                  <path
                    d="M2.5 4L5 6.5L7.5 4"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {zoomOpen && (
                <div className="absolute bottom-full mb-1 left-0 bg-white border border-[#E5E7EB] rounded shadow-md py-1 z-10 min-w-[70px]">
                  {ZOOM_OPTIONS.map((z) => (
                    <button
                      key={z}
                      type="button"
                      onClick={() => {
                        setZoom(z);
                        setZoomOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1 text-xs sm:text-sm font-inter hover:bg-gray-50 ${
                        z === zoom ? "text-[#0DB5B5] font-medium" : "text-[#1F1F1F]"
                      }`}
                    >
                      {z}%
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sheet tabs — one per parsed worksheet */}
            <div className="flex items-center gap-1 overflow-x-auto flex-1 custom-scrollbar-overlay">
              {sheets.map((sheet) => {
                const isActive = sheet.name === activeSheetName;
                return (
                  <button
                    key={sheet.name}
                    type="button"
                    onClick={() => setActiveSheetName(sheet.name)}
                    className={`px-3 py-1 text-xs sm:text-sm font-inter whitespace-nowrap border-t-[2px] transition-colors ${
                      isActive
                        ? "border-t-[#0DB5B5] text-[#0DB5B5] font-medium bg-white"
                        : "border-t-transparent text-[#6B7280] hover:bg-gray-50"
                    }`}
                  >
                    {sheet.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        )}

        {/* Footer — collapses to a single Close button once a result is shown
            since there's no import action left to take. */}
        <div className="border-t border-[#E8E8E8] px-6 py-4 flex justify-end gap-3 flex-shrink-0">
          {showingResult ? (
            <Button variant="primary" size="md" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                size="md"
                onClick={onClose}
                disabled={uploadInFlight}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => onImport(file)}
                disabled={loading || !!error || uploadInFlight}
              >
                {uploadInFlight ? inFlightLabel : primaryLabel}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BulkUploadPreviewModal;
